import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchableOption {
  value: number;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: number | null;
  onChange: (value: number, option: SearchableOption) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * A select you can type into.
 *
 * A plain <select> is unusable past a few dozen entries — the destination lists here run to
 * 189 countries and over 6000 cities, in no particular order, with no way to search. Native
 * type-ahead does not help either: the shipping API spells the United States "United States
 * Of America", so typing "USA" matches nothing.
 *
 * Options are sorted by label; typing filters them.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  emptyText = 'No matches',
  disabled = false,
  className = '',
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const sorted = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    // Matches anywhere in the label, so "angeles" finds "Los Angeles" and "states" finds
    // "United States Of America".
    return sorted.filter((o) => o.label.toLowerCase().includes(q));
  }, [sorted, query]);

  // Close when the click lands outside, leaving the current selection untouched.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function choose(option: SearchableOption) {
    onChange(option.value, option);
    setOpen(false);
    setQuery('');
  }

  const inputCls =
    'w-full px-4 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20 disabled:opacity-50';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        disabled={disabled}
        className={inputCls}
        placeholder={selected ? selected.label : placeholder}
        value={open ? query : selected?.label ?? ''}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
          }
          if (e.key === 'Enter' && open && filtered.length > 0) {
            e.preventDefault();
            choose(filtered[0] as SearchableOption);
          }
        }}
      />

      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-black/[0.08] dark:border-white/[0.12] bg-white dark:bg-[#111] shadow-xl"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-black/40 dark:text-white/40">{emptyText}</li>
          )}
          {/* Capped for rendering cost only — the search narrows well before this matters. */}
          {filtered.slice(0, 200).map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => choose(option)}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-black/[0.05] dark:hover:bg-white/[0.08] ${
                  option.value === value ? 'font-bold' : 'font-medium'
                }`}
              >
                {option.label}
              </button>
            </li>
          ))}
          {filtered.length > 200 && (
            <li className="px-4 py-2 text-[11px] text-black/30 dark:text-white/30">
              +{filtered.length - 200} more — keep typing to narrow
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
